import { DateBox, RadioGroup, SelectBox, Switch, TagBox } from "devextreme-react"
import { Checklist, ScheduleFrequencyDays, ScheduleMonthlyFrequencyDays, ScheduleMonthlyFrequencyMonths, ScheduleMonthlyFrequencyOns, ScheduleType, ScheduleWeeklyFrequencyDay } from "../model/ChecklistDtos"
import { t } from "i18next"
import { LoadIndicator, Row, ValidationError } from "../../../core/components"

export interface FormState {
    idChecklist?: number
    idChecklistSchedule?: number
    onceDate?: string
    startDate?: string
    endDate?: string
    enableRepeat: boolean
    repeatType: ScheduleType
    repeatOnDayDaily: ScheduleFrequencyDays[]
    repeatOnDayWeekly?: ScheduleWeeklyFrequencyDay
    repeatOnMonths: ScheduleMonthlyFrequencyMonths[]
    repeatOnWeekdayOnMonths: boolean
    monthlyFrequencyDay: ScheduleMonthlyFrequencyDays[]
    monthlyOnWeekDays: ScheduleMonthlyFrequencyOns[]
    monthlyFrequencyOnWeekDay: ScheduleFrequencyDays[]
}

interface ScheduleFormProps {
    loading: boolean
    checklists: Checklist[]
    initialData?: Partial<FormState>
    defaultDate?: Date
    validationErrors: Record<string, string>
    usageContext: "checklist" | "calendar"
    handleChange: (updates: Partial<FormState>) => void
    resetValidationErrors: () => void
}

export default function ScheduleForm({
    initialData,
    defaultDate,
    checklists,
    handleChange,
    validationErrors,
    resetValidationErrors,
    loading,
    usageContext,
}: ScheduleFormProps) {
    const checklistSelectionEnabled = usageContext === 'calendar' && !initialData?.idChecklistSchedule

    return <>
        {!initialData
            ? <LoadIndicator />
            : <>
            {loading && <LoadIndicator loading />}
            <DateBox
                label={`${t("Date")} *`}
                type="date"
                value={initialData.onceDate ?? new Date()}
                onValueChanged={(e) => handleChange({ onceDate: e.value, startDate: e.value })}
                calendarOptions={{ firstDayOfWeek: 1 }}
                displayFormat={"yyyy/MM/dd"}
                useMaskBehavior={true}
                dateSerializationFormat="yyyy-MM-dd"
                visible={initialData.repeatType === ScheduleType.Once}
                pickerType="calendar"
                stylingMode="outlined"
                disabled={loading}
                isValid={!validationErrors.onceDate} >
            </DateBox>
                <ValidationError message={validationErrors.onceDate} />

                {initialData.repeatType !== ScheduleType.Once && <Row alignItems="center" grow={1}>
                    <Row.Col>
                        <DateBox
                            label={`${t("Start Date")} *`}
                            value={initialData.startDate || (defaultDate ?? new Date())}
                            defaultValue={defaultDate ?? new Date()}
                            type="date"
                            stylingMode="outlined"
                            onValueChanged={e => handleChange({ startDate: e.value, onceDate: e.value })}
                            displayFormat={"yyyy/MM/dd"}
                            calendarOptions={{ firstDayOfWeek: 1 }}
                            dateSerializationFormat="yyyy-MM-dd"
                            useMaskBehavior={true}
                            pickerType="calendar"
                            disabled={loading}
                            visible={
                                initialData.repeatType === ScheduleType.Daily ||
                                initialData.repeatType === ScheduleType.Weekly ||
                                initialData.repeatType === ScheduleType.Monthly ||
                                initialData.repeatType === ScheduleType.FourWeekly
                            } />
                        <ValidationError message={validationErrors.startDate} />
                    </Row.Col>

                    <Row.Col>
                        <DateBox
                            label={t("End Date")}
                            value={initialData.endDate || null}
                            type="date"
                            stylingMode="outlined"
                            onValueChanged={(e) => handleChange({ endDate: e.value })}
                            calendarOptions={{ firstDayOfWeek: 1 }}
                            displayFormat="yyyy/MM/dd"
                            useMaskBehavior={true}
                            dateSerializationFormat="yyyy-MM-dd"
                            defaultValue={null}
                            pickerType="calendar"
                            min={initialData.startDate}
                            showClearButton
                            disabled={loading}
                            visible={
                                initialData.repeatType === ScheduleType.Daily ||
                                initialData.repeatType === ScheduleType.Weekly ||
                                initialData.repeatType === ScheduleType.Monthly ||
                                initialData.repeatType === ScheduleType.FourWeekly
                            } />
                    </Row.Col>
                </Row>}

                <SelectBox
                    label={`${t("Checklist")} *`}
                    value={usageContext === 'checklist' ?  checklists[0].idChecklist : initialData.idChecklist }
                    readOnly={usageContext === 'checklist' || !!initialData.idChecklistSchedule}
                    searchEnabled={checklistSelectionEnabled}
                    focusStateEnabled={checklistSelectionEnabled}
                    hoverStateEnabled={checklistSelectionEnabled}
                    activeStateEnabled={checklistSelectionEnabled}
                    onValueChanged={e => handleChange({ idChecklist: e.value })}
                    dataSource={checklists}
                    displayExpr="name"
                    valueExpr="idChecklist"
                    placeholder="Select checklist"
                    stylingMode="outlined"
                    disabled={loading}
                    isValid={!validationErrors.idChecklist} />
                <ValidationError message={validationErrors.idChecklist} />

                <div style={{ display: "inline-flex", gap: "0.5rem" }}>
                    <Switch
                        value={initialData.repeatType !== ScheduleType.Once}
                        stylingMode="outlined"
                        disabled={loading}
                        onValueChanged={(e: any) => {
                            if (!e.value) {
                                handleChange({ enableRepeat: false, endDate: undefined, repeatType: ScheduleType.Once })
                            } else {
                                handleChange({ enableRepeat: true, repeatType: ScheduleType.Daily })
                            }
                            resetValidationErrors()
                        }}
                    />
                    <span>{t("Repeat")}</span>
                </div>

                <SelectBox
                    dataSource={[
                        {
                            id: ScheduleType.Daily,
                            name: t("Daily"),
                            config: { type: "weeks", step: 1, days: true },
                        },
                        {
                            id: ScheduleType.Weekly,
                            name: t("Weekly"),
                            config: { type: "weeks", step: 1, days: true },
                        },
                        {
                            id: ScheduleType.FourWeekly,
                            name: t("Four Weekly"),
                            config: { type: "weeks", step: 1, days: true },
                        },
                        {
                            id: ScheduleType.Monthly,
                            name: t("Monthly"),
                            config: { type: "month", step: 1, days: true },
                        },
                    ]}
                    valueExpr={"id"}
                    displayExpr={"name"}
                    value={initialData.repeatType}
                    onValueChanged={(e: { component?: any; value?: any }): void => {
                        if (e.value === initialData.repeatType) {
                            return
                        }
                        handleChange({
                            repeatType: e.value,
                            repeatOnDayDaily: [],
                            repeatOnDayWeekly: undefined,
                            repeatOnMonths: [],
                            // repeatEvery: 0,
                            monthlyFrequencyDay: [],
                            monthlyFrequencyOnWeekDay: [],
                            monthlyOnWeekDays: []
                        })
                        resetValidationErrors()
                    }}
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType !== ScheduleType.Once}
                    isValid={!validationErrors.repeatType} />
                <ValidationError message={validationErrors.repeatType} />

                <TagBox
                    label={`${t("Repeat On (Days)")} *`}
                    value={initialData.repeatOnDayDaily}
                    onValueChanged={e => handleChange({ repeatOnDayDaily: e.value })}
                    items={[
                        { day: t("Monday"), value: ScheduleFrequencyDays.Monday },
                        { day: t("Tuesday"), value: ScheduleFrequencyDays.Tuesday },
                        { day: t("Wednesday"), value: ScheduleFrequencyDays.Wednesday },
                        { day: t("Thursday"), value: ScheduleFrequencyDays.Thursday },
                        { day: t("Friday"), value: ScheduleFrequencyDays.Friday },
                        { day: t("Saturday"), value: ScheduleFrequencyDays.Saturday },
                        { day: t("Sunday"), value: ScheduleFrequencyDays.Sunday }
                    ]}
                    displayExpr={"day"}
                    valueExpr={"value"}
                    maxDisplayedTags={3}
                    showClearButton
                    applyValueMode="useButtons"
                    showSelectionControls
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType === ScheduleType.Daily}
                    isValid={!validationErrors.repeatOnDayDaily} />
                <ValidationError message={validationErrors.repeatOnDayDaily} />

                <SelectBox
                    label={`${t("Repeat On (Day)")} *`}
                    value={initialData.repeatOnDayWeekly}
                    onValueChanged={e => handleChange({ repeatOnDayWeekly: e.value })}
                    items={[
                        { day: t("Monday"), value: ScheduleWeeklyFrequencyDay.Monday },
                        { day: t("Tuesday"), value: ScheduleWeeklyFrequencyDay.Tuesday },
                        { day: t("Wednesday"), value: ScheduleWeeklyFrequencyDay.Wednesday },
                        { day: t("Thursday"), value: ScheduleWeeklyFrequencyDay.Thursday },
                        { day: t("Friday"), value: ScheduleWeeklyFrequencyDay.Friday },
                        { day: t("Saturday"), value: ScheduleWeeklyFrequencyDay.Saturday },
                        { day: t("Sunday"), value: ScheduleWeeklyFrequencyDay.Sunday }
                    ]}
                    displayExpr={"day"}
                    valueExpr={"value"}
                    showClearButton
                    disabled={loading}
                    visible={
                        initialData.repeatType === ScheduleType.Weekly ||
                        initialData.repeatType === ScheduleType.FourWeekly
                    }
                    stylingMode="outlined"
                    isValid={!validationErrors.repeatOnDayWeekly} />
                <ValidationError message={validationErrors.repeatOnDayWeekly} />

                <TagBox
                    label={`${t("Repeat On (Months)")} *`}
                    value={initialData.repeatOnMonths}
                    onValueChanged={e => handleChange({ repeatOnMonths: e.value })}
                    items={[
                        { month: t("January"), value: ScheduleMonthlyFrequencyMonths.January },
                        { month: t("February"), value: ScheduleMonthlyFrequencyMonths.February },
                        { month: t("March"), value: ScheduleMonthlyFrequencyMonths.March },
                        { month: t("April"), value: ScheduleMonthlyFrequencyMonths.April },
                        { month: t("May"), value: ScheduleMonthlyFrequencyMonths.May },
                        { month: t("June"), value: ScheduleMonthlyFrequencyMonths.June },
                        { month: t("July"), value: ScheduleMonthlyFrequencyMonths.July },
                        { month: t("August"), value: ScheduleMonthlyFrequencyMonths.August },
                        { month: t("September"), value: ScheduleMonthlyFrequencyMonths.September },
                        { month: t("October"), value: ScheduleMonthlyFrequencyMonths.October },
                        { month: t("November"), value: ScheduleMonthlyFrequencyMonths.November },
                        { month: t("December"), value: ScheduleMonthlyFrequencyMonths.December },
                    ]}
                    applyValueMode="useButtons"
                    displayExpr={"month"}
                    valueExpr={"value"}
                    maxDisplayedTags={3}
                    showClearButton
                    showSelectionControls
                    disabled={loading}
                    stylingMode="outlined"
                    visible={initialData.repeatType === ScheduleType.Monthly}
                    isValid={!validationErrors.repeatOnMonths} />
                <ValidationError message={validationErrors.repeatOnMonths} />

                <RadioGroup
                    value={initialData.repeatOnWeekdayOnMonths}
                    onValueChanged={e => handleChange({
                        repeatOnWeekdayOnMonths: e.value,
                        monthlyFrequencyDay: [],
                        monthlyFrequencyOnWeekDay: [],
                        monthlyOnWeekDays: []
                    })}
                    items={[
                        { label: t("Days"), value: false },
                        { label: t("On"), value: true }
                    ]}
                    valueExpr={"value"}
                    displayExpr={"label"}
                    layout="horizontal"
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType === ScheduleType.Monthly} />

                <TagBox
                    label={`${t("Repeat On (Days)")} *`}
                    value={initialData.monthlyFrequencyDay}
                    onValueChanged={e => handleChange({ monthlyFrequencyDay: e.value })}
                    applyValueMode="useButtons"
                    items={[
                        { day: t("Day1"), value: ScheduleMonthlyFrequencyDays.Day1 },
                        { day: t("Day2"), value: ScheduleMonthlyFrequencyDays.Day2 },
                        { day: t("Day3"), value: ScheduleMonthlyFrequencyDays.Day3 },
                        { day: t("Day4"), value: ScheduleMonthlyFrequencyDays.Day4 },
                        { day: t("Day5"), value: ScheduleMonthlyFrequencyDays.Day5 },
                        { day: t("Day6"), value: ScheduleMonthlyFrequencyDays.Day6 },
                        { day: t("Day7"), value: ScheduleMonthlyFrequencyDays.Day7 },
                        { day: t("Day8"), value: ScheduleMonthlyFrequencyDays.Day8 },
                        { day: t("Day9"), value: ScheduleMonthlyFrequencyDays.Day9 },
                        { day: t("Day10"), value: ScheduleMonthlyFrequencyDays.Day10 },
                        { day: t("Day11"), value: ScheduleMonthlyFrequencyDays.Day11 },
                        { day: t("Day12"), value: ScheduleMonthlyFrequencyDays.Day12 },
                        { day: t("Day13"), value: ScheduleMonthlyFrequencyDays.Day13 },
                        { day: t("Day14"), value: ScheduleMonthlyFrequencyDays.Day14 },
                        { day: t("Day15"), value: ScheduleMonthlyFrequencyDays.Day15 },
                        { day: t("Day16"), value: ScheduleMonthlyFrequencyDays.Day16 },
                        { day: t("Day17"), value: ScheduleMonthlyFrequencyDays.Day17 },
                        { day: t("Day18"), value: ScheduleMonthlyFrequencyDays.Day18 },
                        { day: t("Day19"), value: ScheduleMonthlyFrequencyDays.Day19 },
                        { day: t("Day20"), value: ScheduleMonthlyFrequencyDays.Day20 },
                        { day: t("Day21"), value: ScheduleMonthlyFrequencyDays.Day21 },
                        { day: t("Day22"), value: ScheduleMonthlyFrequencyDays.Day22 },
                        { day: t("Day23"), value: ScheduleMonthlyFrequencyDays.Day23 },
                        { day: t("Day24"), value: ScheduleMonthlyFrequencyDays.Day24 },
                        { day: t("Day25"), value: ScheduleMonthlyFrequencyDays.Day25 },
                        { day: t("Day26"), value: ScheduleMonthlyFrequencyDays.Day26 },
                        { day: t("Day27"), value: ScheduleMonthlyFrequencyDays.Day27 },
                        { day: t("Day28"), value: ScheduleMonthlyFrequencyDays.Day28 },
                        { day: t("Day29"), value: ScheduleMonthlyFrequencyDays.Day29 },
                        { day: t("Day30"), value: ScheduleMonthlyFrequencyDays.Day30 },
                        { day: t("Day31"), value: ScheduleMonthlyFrequencyDays.Day31 },
                        { day: t("Last"), value: ScheduleMonthlyFrequencyDays.Last }
                    ]}
                    displayExpr={"day"}
                    valueExpr={"value"}
                    maxDisplayedTags={3}
                    showClearButton
                    showSelectionControls
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType === ScheduleType.Monthly && !initialData.repeatOnWeekdayOnMonths}
                    isValid={!validationErrors.monthlyFrequencyDay} />
                <ValidationError message={validationErrors.monthlyFrequencyDay} />

                <TagBox
                    label={`${t("Repeat On")} *`}
                    value={initialData.monthlyOnWeekDays}
                    onValueChanged={e => handleChange({ monthlyOnWeekDays: e.value })}
                    items={[
                        { week: t("First"), value: ScheduleMonthlyFrequencyOns.First },
                        { week: t("Second"), value: ScheduleMonthlyFrequencyOns.Second },
                        { week: t("Third"), value: ScheduleMonthlyFrequencyOns.Third },
                        { week: t("Fourth"), value: ScheduleMonthlyFrequencyOns.Fourth },
                        { week: t("Last"), value: ScheduleMonthlyFrequencyOns.Last },
                    ]}
                    maxDisplayedTags={3}
                    displayExpr={"week"}
                    valueExpr={"value"}
                    showClearButton
                    showSelectionControls
                    applyValueMode="useButtons"
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType === ScheduleType.Monthly && initialData.repeatOnWeekdayOnMonths}
                    isValid={!validationErrors.monthlyOnWeekDays} />
                <ValidationError message={validationErrors.monthlyOnWeekDays} />

                <TagBox
                    label={`${t("Repeat On (Day)")} *`}
                    value={initialData.monthlyFrequencyOnWeekDay}
                    onValueChanged={e => handleChange({ monthlyFrequencyOnWeekDay: e.value })}
                    items={[
                        { day: t("Monday"), value: ScheduleFrequencyDays.Monday },
                        { day: t("Tuesday"), value: ScheduleFrequencyDays.Tuesday },
                        { day: t("Wednesday"), value: ScheduleFrequencyDays.Wednesday },
                        { day: t("Thursday"), value: ScheduleFrequencyDays.Thursday },
                        { day: t("Friday"), value: ScheduleFrequencyDays.Friday },
                        { day: t("Saturday"), value: ScheduleFrequencyDays.Saturday },
                        { day: t("Sunday"), value: ScheduleFrequencyDays.Sunday }
                    ]}
                    maxDisplayedTags={3}
                    displayExpr={"day"}
                    valueExpr={"value"}
                    applyValueMode="useButtons"
                    showClearButton
                    showSelectionControls
                    stylingMode="outlined"
                    disabled={loading}
                    visible={initialData.repeatType === ScheduleType.Monthly && initialData.repeatOnWeekdayOnMonths}
                    isValid={!validationErrors.monthlyFrequencyOnWeekDay} />
                <ValidationError message={validationErrors.monthlyFrequencyOnWeekDay} />
            </>}
    </>
}
